import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Share2, MessageCircle, Send, QrCode, ChevronDown } from 'lucide-react';
import type { WeatherData } from '../../hooks/useWeather';
import { getWeatherGradient, getWeatherIcon, translateWeatherDescription } from '../../utils/weather';
import type { SharePlatform } from '../../utils/shareUtils';
import { buildShareContent, getQrCodeUrl, openShareLink } from '../../utils/shareUtils';

interface WeatherCardProps {
  weather: WeatherData;
  location?: string;
}

export default function WeatherCard({ weather, location }: WeatherCardProps) {
  const { t } = useTranslation();
  const [shareStatus, setShareStatus] = useState<'idle' | 'copied' | 'error'>('idle');
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const gradient = getWeatherGradient(weather.weather_code);
  const Icon = getWeatherIcon(weather.icon);

  const shareContent = useMemo(() => {
    const baseUrl = typeof window !== 'undefined'
      ? `${window.location.origin}${location ? `?location=${encodeURIComponent(location)}` : ''}`
      : '';
    return buildShareContent({
      title: t('share.weatherTitle', { location: location || t('share.yourArea') }),
      text: t('share.weatherText', {
        location: location || t('share.yourArea'),
        temp: Math.round(weather.temperature),
        description: weather.description,
      }),
      url: baseUrl,
    });
  }, [location, t, weather.description, weather.temperature]);

  const handleNativeShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share(shareContent);
        setShareStatus('idle');
        return;
      }

      await navigator.clipboard?.writeText(`${shareContent.text} ${shareContent.url}`);
      setShareStatus('copied');
    } catch {
      setShareStatus('error');
    } finally {
      setTimeout(() => setShareStatus('idle'), 2500);
    }
  };

  const handlePlatformShare = (platform: SharePlatform) => {
    openShareLink(platform, shareContent);
  };

  const handleQrShare = () => {
    const qrUrl = getQrCodeUrl(shareContent.url);
    if (typeof window !== 'undefined') {
      window.open(qrUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <motion.div
      className={`p-8 rounded-3xl ${gradient} text-white shadow-2xl`}
      data-testid="weather-card"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex items-start justify-between mb-8 gap-4">
        <div>
          {location && (
            <motion.h3
              className="text-2xl font-semibold mb-2"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              {location}
            </motion.h3>
          )}
          <motion.h2
            className="text-6xl font-bold mb-2"
            data-testid="current-temperature"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            {Math.round(weather.temperature)}°C
          </motion.h2>
          <p className="text-xl opacity-90">
            {t('weather.feelsLike')} {Math.round(weather.feels_like)}°C
          </p>
        </div>

        <div className="flex flex-col items-end gap-3">
          <div className="relative">
            <motion.button
              onClick={() => setShareMenuOpen(!shareMenuOpen)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors backdrop-blur-sm border border-white/20"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Share2 className="w-4 h-4" />
              {t('share.native')}
              <ChevronDown className={`w-4 h-4 transition-transform ${shareMenuOpen ? 'rotate-180' : ''}`} />
            </motion.button>

            <AnimatePresence>
              {shareMenuOpen && (
                <motion.div
                  className="absolute top-full right-0 mt-2 bg-surface rounded-lg border border-border shadow-xl z-50 min-w-max"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.button
                    onClick={() => { handleNativeShare(); setShareMenuOpen(false); }}
                    className="w-full px-4 py-2 text-left hover:bg-surface-elevated first:rounded-t-lg flex items-center gap-3 text-text-primary transition-colors"
                    whileHover={{ x: 4 }}
                  >
                    <Share2 className="w-4 h-4" />
                    {t('share.native')}
                  </motion.button>
                  <motion.button
                    onClick={() => { handlePlatformShare('twitter'); setShareMenuOpen(false); }}
                    className="w-full px-4 py-2 text-left hover:bg-surface-elevated flex items-center gap-3 text-text-primary transition-colors"
                    whileHover={{ x: 4 }}
                  >
                    <Send className="w-4 h-4" />
                    {t('share.twitterX')}
                  </motion.button>
                  <motion.button
                    onClick={() => { handlePlatformShare('facebook'); setShareMenuOpen(false); }}
                    className="w-full px-4 py-2 text-left hover:bg-surface-elevated flex items-center gap-3 text-text-primary transition-colors"
                    whileHover={{ x: 4 }}
                  >
                    <Share2 className="w-4 h-4" />
                    {t('share.facebook')}
                  </motion.button>
                  <motion.button
                    onClick={() => { handlePlatformShare('whatsapp'); setShareMenuOpen(false); }}
                    className="w-full px-4 py-2 text-left hover:bg-surface-elevated flex items-center gap-3 text-text-primary transition-colors"
                    whileHover={{ x: 4 }}
                  >
                    <MessageCircle className="w-4 h-4" />
                    {t('share.whatsapp')}
                  </motion.button>
                  <motion.button
                    onClick={() => { handleQrShare(); setShareMenuOpen(false); }}
                    className="w-full px-4 py-2 text-left hover:bg-surface-elevated last:rounded-b-lg flex items-center gap-3 text-text-primary transition-colors"
                    whileHover={{ x: 4 }}
                  >
                    <QrCode className="w-4 h-4" />
                    {t('share.qrCode')}
                  </motion.button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <motion.div
            className="w-32 h-32"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Icon className="w-full h-full" />
          </motion.div>
          {shareStatus === 'copied' && (
            <motion.span
              className="text-xs bg-white/10 px-2 py-1 rounded-md"
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {t('share.copied')}
            </motion.span>
          )}
          {shareStatus === 'error' && (
            <motion.span
              className="text-xs bg-danger/20 px-2 py-1 rounded-md"
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {t('share.shareFallback')}
            </motion.span>
          )}
        </div>
      </div>

      <motion.p 
        className="text-2xl font-medium mb-6"
        data-testid="weather-description"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        {translateWeatherDescription(weather.description, t)}
      </motion.p>

      <div className="grid grid-cols-2 gap-4" data-testid="weather-stats">
        <StatItem label={t('weather.humidity')} value={`${weather.humidity}%`} />
        <StatItem label={t('weather.wind')} value={`${weather.wind_speed} km/h`} />
        <StatItem label={t('weather.visibility')} value={`${weather.visibility} km`} />
        <StatItem label={t('weather.pressure')} value={`${weather.pressure} hPa`} />
      </div>
    </motion.div>
  );
}

function StatItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
      <p className="text-sm opacity-80 mb-1">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}
